/**
 * Map the API's raw embed-config (GET /api/public/embed-config → services/api handler/public.go)
 * into the SPA's EmbedConfig + the intake category list. The API returns a single `themeColor`;
 * we derive the light+dark accent ramps client-side (theme/contrast.ts) exactly as theming.md §5
 * describes — the SAME derivation the Branding admin previews, so a town's saved color themes the
 * SPA + widget consistently. `categories` drive the intake radio-cards (dept routing).
 */
import type { EmbedConfig, AccentRamp } from "../theme/embedConfig.js";
import { DEFAULT_EMBED_CONFIG } from "../theme/mockEmbedConfig.js";
import { deriveAccentRamp } from "../theme/contrast.js";
import type { IntakeCategory } from "./categories.js";
import type { QuestionTopic } from "./questionTopics.js";
import type { ChannelView } from "./types.js";
import type { KORARecordType } from "./koraRecordTypes.js";
import type { PermitType } from "./permitTypes.js";
import type { FormSchema, InspectionTypeDef, RequiredDoc } from "./types.js";

/** State-configurable Open Records config (records-request.md "STATE-CONFIGURABLE, not hardcoded
 *  law"): ships the Kansas KORA profile as the v1 DEFAULT, but both fields are per-tenant
 *  TenantSettings.KORAConfig — never hardcoded legal text baked into the UI. */
export interface KORAEmbedConfig {
  respondDays: number;
  citationLabel: string;
}

export const DEFAULT_KORA_CONFIG: KORAEmbedConfig = {
  respondDays: 3,
  citationLabel: "Kansas Open Records Act (KORA)",
};

/** Category as returned by the API (shared.Category). */
interface ApiCategory {
  id: string;
  label: string;
  order?: number;
  active?: boolean;
  /** Channel-only (stay-informed-channels.md §2.1) — absent/false for concern/question rows. */
  defaultOn?: boolean;
  /** PERMIT-TYPE-ONLY (Kind="permit" — permits-fidelity.md §A.1): the versioned per-type guided
   *  application form + its required-docs checklist. Undefined for every other Kind, and for a
   *  legacy permit type seeded before Phase A. */
  formSchema?: FormSchema;
  requiredDocs?: RequiredDoc[];
  /** PERMIT-TYPE-ONLY (permit-type-fee-override): per-type application-fee override in whole dollars.
   *  `0` = free (Water), a positive number overrides the town default, absent = inherit the default.
   *  Emitted by public.go (full shared.Category; omitempty on a nil pointer, so `0` DOES serialize). */
  permitAppFeeOverride?: number;
  /** PERMIT-TYPE-ONLY (permit-inspections.md Phase 1): the ordered expected-inspection checklist.
   *  Undefined/empty means "no checklist" (e.g. Water). Rides along automatically (full
   *  shared.Category serialized as-is) same as formSchema/requiredDocs above. */
  inspectionTypes?: InspectionTypeDef[];
}

/** Raw GET /api/public/embed-config body. `categories` is Kind=concern-only,
 *  `questionTopics` is Kind=question-only, `channels` is Kind=channel-only, `koraRecordTypes`
 *  is Kind=kora-only, and `permitTypes` is Kind=permit-only — the server splits these
 *  (handler/public.go EmbedConfig, ask-a-question-faq.md §2.1 / stay-informed-channels.md §2.1 /
 *  records-request.md / roadmap module 6), so no list can ever mix another Kind's rows. `kora` is
 *  the safe-to-expose {respondDays, citationLabel} config block (TenantSettings.KORAConfig) — never
 *  a fee schedule or exemption-code list. */
export interface RawEmbedConfig {
  townName?: string;
  themeColor?: string | null;
  /** Page background color pulled from the town's site (TenantSettings.backgroundColor). */
  backgroundColor?: string | null;
  /** The town's own public homepage (TenantSettings.websiteUrl) — the "Back to the city site"
   *  destination on the standalone notify pages. Absent/empty when the town hasn't set one. */
  websiteUrl?: string | null;
  /** The town's own public contact details (TenantSettings) — surfaced on the printed agenda
   *  letterhead/footer. Absent/empty when unset. */
  townMailingAddress?: string | null;
  townPhone?: string | null;
  townEmail?: string | null;
  logo?: string | null;
  /** `{display, body}` (either may be empty). A LEGACY plain string is also accepted from an older
   *  save and applies to BOTH the display and body stacks (#172). */
  fonts?: { display?: string; body?: string } | string | null;
  allowedEmbedOrigins?: string[];
  categories?: ApiCategory[];
  questionTopics?: ApiCategory[];
  channels?: ApiCategory[];
  koraRecordTypes?: ApiCategory[];
  permitTypes?: ApiCategory[];
  kora?: { respondDays?: number; citationLabel?: string };
  /** Council Agenda + Packet public-display toggles (meetings-public-relaunch.md §3) — the server
   *  ALWAYS returns the RESOLVED boolean (default true), never omitted, and already ENFORCES these
   *  server-side by omitting narrativeHtml/attachments from the public meeting detail when false;
   *  the client mainly reads these to avoid pre-rendering a layout that expects data that won't
   *  arrive (e.g. a "supporting docs" affordance) — see PublicMeetingView.tsx. */
  publicShowAgendaItemDetails?: boolean;
  publicShowAttachments?: boolean;
}

/**
 * Normalize the wire `fonts` field (RawEmbedConfig.fonts) to the SPA's `{display, body}` shape
 * (#172). The backend's `fonts` is an OBJECT going forward; a LEGACY plain string (pre-#172 saves)
 * is mapped to BOTH pickers so an existing tenant's saved font keeps rendering unchanged.
 */
function normalizeFonts(
  fonts: { display?: string; body?: string } | string | null | undefined,
): { display?: string; body?: string } | undefined {
  if (!fonts) return undefined;
  if (typeof fonts === "string") return { display: fonts, body: fonts };
  if (!fonts.display && !fonts.body) return undefined;
  return { display: fonts.display, body: fonts.body };
}

/**
 * Derive the dark-mode base from the light hex: the tokens keep the SAME hue but a lighter shade
 * reads on the near-black canvas. We lighten the chosen color ~30% for the dark base, then run the
 * same ramp derivation. (The server owns the authoritative derivation; this mirrors it for display.)
 */
function darkRampFrom(hex: string): AccentRamp {
  const light = deriveAccentRamp(hex);
  if (!light) return DEFAULT_EMBED_CONFIG.accent.dark;
  // Lighten for dark mode by deriving a ramp from a lightened base.
  const lightened = lighten(light.base, 0.34);
  const dark = deriveAccentRamp(lightened) ?? light;
  return { ...dark, contrast: "#0F1712" };
}

function lighten(hex: string, amount: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const up = (c: number) => Math.round(c + (255 - c) * amount);
  const h = (c: number) => up(c).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

export function deriveEmbedConfig(raw: RawEmbedConfig): {
  config: EmbedConfig;
  categories: IntakeCategory[];
  questionTopics: QuestionTopic[];
  channels: ChannelView[];
  koraRecordTypes: KORARecordType[];
  permitTypes: PermitType[];
  kora: KORAEmbedConfig;
  publicShowAgendaItemDetails: boolean;
  publicShowAttachments: boolean;
} {
  const themeColor = raw.themeColor?.trim() || null;
  const lightRamp = themeColor ? deriveAccentRamp(themeColor) : null;

  const config: EmbedConfig = {
    tenantId: "linnvalley",
    townName: raw.townName || DEFAULT_EMBED_CONFIG.townName,
    logoUrl: raw.logo || undefined,
    fonts: normalizeFonts(raw.fonts),
    privacyUrl: "#privacy",
    websiteUrl: raw.websiteUrl?.trim() || undefined,
    townMailingAddress: raw.townMailingAddress?.trim() || undefined,
    townPhone: raw.townPhone?.trim() || undefined,
    townEmail: raw.townEmail?.trim() || undefined,
    accent: lightRamp
      ? { light: lightRamp, dark: darkRampFrom(themeColor!) }
      : DEFAULT_EMBED_CONFIG.accent,
    backgroundColor: raw.backgroundColor?.trim() || undefined,
  };

  const categories: IntakeCategory[] = (raw.categories ?? [])
    .filter((c) => c.active !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((c) => ({
      id: c.id,
      label: c.label,
      // The API category has no explicit department; the label carries routing context in v1.
      department: departmentFor(c.label),
    }));

  const questionTopics: QuestionTopic[] = (raw.questionTopics ?? [])
    .filter((t) => t.active !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((t) => ({ id: t.id, label: t.label }));

  const channels: ChannelView[] = (raw.channels ?? [])
    .filter((c) => c.active !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((c) => ({
      id: c.id,
      label: c.label,
      order: c.order ?? 0,
      defaultOn: c.defaultOn === true,
    }));

  const koraRecordTypes: KORARecordType[] = (raw.koraRecordTypes ?? [])
    .filter((t) => t.active !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((t) => ({ id: t.id, label: t.label }));

  const permitTypes: PermitType[] = (raw.permitTypes ?? [])
    .filter((t) => t.active !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((t) => ({
      id: t.id,
      label: t.label,
      formSchema: t.formSchema,
      requiredDocs: t.requiredDocs,
      permitAppFeeOverride: t.permitAppFeeOverride,
      inspectionTypes: t.inspectionTypes,
    }));

  const kora: KORAEmbedConfig = {
    respondDays: raw.kora?.respondDays ?? DEFAULT_KORA_CONFIG.respondDays,
    citationLabel: raw.kora?.citationLabel?.trim() || DEFAULT_KORA_CONFIG.citationLabel,
  };

  return {
    config,
    categories,
    questionTopics,
    channels,
    koraRecordTypes,
    permitTypes,
    kora,
    publicShowAgendaItemDetails: raw.publicShowAgendaItemDetails !== false,
    publicShowAttachments: raw.publicShowAttachments !== false,
  };
}

/** Best-effort routing hint from the label (the API doesn't yet carry a department field). */
function departmentFor(label: string): string {
  const l = label.toLowerCase();
  if (l.includes("pothole") || l.includes("street") || l.includes("drain") || l.includes("water"))
    return "Public Works";
  if (l.includes("something") || l.includes("other"))
    return "We'll route it to the right department";
  return "Code Enforcement";
}
